import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './Design.css';

const API_KEY = process.env.REACT_APP_API_KEY;

const fetchTimetableData = async (schoolCode, eduCode, grade, className, startDate, endDate, schoolType) => {
  try {
    let endpoint = '';
    
    if (schoolType === '중학교') {
      endpoint = 'https://open.neis.go.kr/hub/misTimetable';
    } else if (schoolType === '고등학교') {
      endpoint = 'https://open.neis.go.kr/hub/hisTimetable';
    } else if (schoolType === '초등학교') {
      endpoint = 'https://open.neis.go.kr/hub/elsTimetable';
    }

    const response = await axios.get(
      `${endpoint}?KEY=${API_KEY}&Type=JSON&pSize=100&pIndex=1&ATPT_OFCDC_SC_CODE=${eduCode}&SD_SCHUL_CODE=${schoolCode}&GRADE=${grade}&CLASS_NM=${className}&TI_FROM_YMD=${startDate}&TI_TO_YMD=${endDate}`
    );
    return response.data;
  } catch (error) {
    console.error("시간표 데이터를 불러오지 못했습니다.", error);
    return null;
  }
};

const fetchSchoolInfo = async (schoolName) => {
  try {
    const response = await axios.get(
      `https://open.neis.go.kr/hub/schoolInfo?KEY=${API_KEY}&Type=JSON&SCHUL_NM=${schoolName}`
    );
    return response.data;
  } catch (error) {
    console.error("학교 정보를 불러오지 못했습니다.", error);
    return null;
  }
};

const getWeekDates = (currentDate) => {
  const startDate = new Date(currentDate);
  const day = startDate.getDay();
  const diff = startDate.getDate() - day + (day === 0 ? -6 : 1);
  const monday = new Date(startDate.setDate(diff));

  const weekDates = [];
  const weekDays = ['월', '화', '수', '목', '금']; // 요일 배열

  for (let i = 0; i < 5; i++) {
    const date = new Date(monday);
    date.setDate(monday.getDate() + i);
    const formattedDate = date.toISOString().slice(0, 10).replace(/-/g, '');
    weekDates.push({ date: formattedDate, day: weekDays[i] }); // 날짜와 요일 함께 저장
  }

  return weekDates;
};

const addDays = (date, days) => {
  const newDate = new Date(date);
  newDate.setDate(newDate.getDate() + days);
  return newDate;
};

const SchoolTimetablePage = () => {
  const [schoolName, setSchoolName] = useState('');
  const [schoolList, setSchoolList] = useState([]);
  const [selectedSchool, setSelectedSchool] = useState(null);
  const [timetableData, setTimetableData] = useState({});
  const [loading, setLoading] = useState(false);
  const [grade, setGrade] = useState('');
  const [className, setClassName] = useState('');
  const [weekDates, setWeekDates] = useState([]);
  const [currentWeek, setCurrentWeek] = useState(new Date());

  const searchSchool = async () => {
    if (schoolName === '') return;
    const schoolInfo = await fetchSchoolInfo(schoolName);
    
    if (schoolInfo && schoolInfo.schoolInfo && schoolInfo.schoolInfo[1].row) {
      setSchoolList(schoolInfo.schoolInfo[1].row);
    } else {
      setSchoolList([]);
    }
  };

  const fetchTimetable = async () => {
    if (!selectedSchool) return;
    setLoading(true);

    const { SD_SCHUL_CODE, ATPT_OFCDC_SC_CODE, SCHUL_KND_SC_NM } = selectedSchool;
    const timetable = {};

    for (let dateObj of weekDates) {
      const timetableInfo = await fetchTimetableData(SD_SCHUL_CODE, ATPT_OFCDC_SC_CODE, grade, className, dateObj.date, dateObj.date, SCHUL_KND_SC_NM);
      if (timetableInfo && timetableInfo.misTimetable && timetableInfo.misTimetable[1]) {
        const timetableForDate = timetableInfo.misTimetable[1].row.map((item) => ({
          period: item.PERIO,
          subject: item.ITRT_CNTNT,
          date: item.ALL_TI_YMD,
        }));
        timetable[dateObj.date] = timetableForDate;
      } else {
        timetable[dateObj.date] = []; // 시간표 정보가 없을 경우 빈 배열로 설정
      }
    }

    setTimetableData(timetable);
    setLoading(false);
  };

  useEffect(() => {
    const dates = getWeekDates(currentWeek);
    setWeekDates(dates);
  }, [currentWeek]);

  useEffect(() => {
    if (selectedSchool && grade && className) {
      fetchTimetable();
    }
  }, [selectedSchool, grade, className, weekDates]);

  useEffect(() => {
    // 저장된 학교 정보를 로드
    const storedSchool = localStorage.getItem('selectedSchool');
    const storedGrade = localStorage.getItem('grade');
    const storedClassName = localStorage.getItem('className');
    if (storedSchool && storedGrade && storedClassName) {
      setSelectedSchool(JSON.parse(storedSchool));
      setGrade(storedGrade);
      setClassName(storedClassName);
    }
  }, []);

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      searchSchool();
    }
  };

  const handleNextWeek = () => {
    const nextWeek = addDays(currentWeek, 7);
    setCurrentWeek(nextWeek);
  };

  const handlePrevWeek = () => {
    const prevWeek = addDays(currentWeek, -7);
    setCurrentWeek(prevWeek);
  };

  const handleThisWeek = () => {
    setCurrentWeek(new Date());
  };
  const handleSaveSchool = () => {
    // 로컬 스토리지 저장 동의 확인
    const consent = window.confirm('이 사이트는 로컬 스토리지에 사용자 정보를 저장합니다. 동의하시겠습니까?');
    
    if (consent) {
      if (window.confirm('학교 정보와 학년, 반을 로컬 스토리지에 저장하시겠습니까?')) {
        // 학교, 학년, 반 정보 로컬 스토리지에 저장
        localStorage.setItem('selectedSchool', JSON.stringify(selectedSchool));
        localStorage.setItem('grade', grade);
        localStorage.setItem('className', className);
        alert('학교 정보와 학년, 반이 로컬 스토리지에 저장되었습니다.');
      }
    } else {
      alert('로컬 스토리지에 저장하는 데 동의하지 않으면 정보가 저장되지 않습니다.');
    }
  };
  
  return (
    <div className="container">
      <div className="navbar">
        <div className="school-info">스쿨리오</div>
        <div className="menu">
          <a className="menuname" href="/school-meal">급식 메뉴</a>
          <a className="menuname" href="/schedule">학사일정</a>
          <a className="menuname" href="/timetable">시간표 보기</a>
        </div>
      </div>

      <h2 className="header">학교 시간표 조회</h2>
      <div className="search-box">
        <input
          type="text"
          value={schoolName}
          onChange={(e) => setSchoolName(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder="학교 이름을 입력하세요"
        />
        <button onClick={searchSchool}>🔍</button>
      </div>

      {schoolList.length > 0 && (
        <ul className="school-list">
          {schoolList.map((school, index) => (
            <li key={index} onClick={() => setSelectedSchool(school)}>
              {school.SCHUL_NM} ({school.ATPT_OFCDC_SC_NM})
            </li>
          ))}
        </ul>
      )}

      {selectedSchool && (
        <div className="timetable-info">
          <h3>{selectedSchool.SCHUL_NM}의 시간표</h3>
          <div className="filters">
            <input
              type="text"
              value={grade}
              className='filter_input'
              onChange={(e) => setGrade(e.target.value)}
              placeholder="학년"
            />
            <input
              type="text"
              value={className}
              className='filter_input'
              onChange={(e) => setClassName(e.target.value)}
              placeholder="학급명"
            />

            <div className="week-navigation">
              {/* 학교 저장 버튼 */}
              <button className="filter_button" onClick={handleSaveSchool}>정보 저장</button>
            </div>
          </div>

          <div className="week-navigation">
            <button onClick={handlePrevWeek}>저번 주</button>
            <button onClick={handleThisWeek}>이번 주</button>
            <button onClick={handleNextWeek}>다음 주</button>
          </div>

          {loading ? (
            <div className="spinner"></div>
          ) : (
            <table className="timetable-table">
              <thead>
                <tr>
                  <th>교시</th>
                  {weekDates.map((dateObj, idx) => (
                    <th key={idx}>
                      {dateObj.day} <br /> ({dateObj.date.slice(4, 6)}.{dateObj.date.slice(6, 8)})
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {[...Array(7).keys()].map((period) => (
                  <tr key={period}>
                    <td>{period + 1}교시</td>
                    {weekDates.map((dateObj, idx) => {
                      const date = dateObj.date;
                      const timetableForDate = timetableData[date] || [];
                      const subject = timetableForDate.find(item => item.period == period + 1);
                      return (
                        <td key={idx}>{subject ? subject.subject : '-'}</td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
};

export default SchoolTimetablePage;
